/**
 * 알림 시스템 — 명세서 §8
 *
 * 이벤트 enum과 채널 매트릭스는 이 파일이 단일 원천이다.
 * 서버 디스패처(§8.2)와 클라이언트 알림 설정 화면이 같은 정의를 사용한다.
 */
export const NOTIFICATION_CHANNELS = {
    IN_APP: 'IN_APP',
    PUSH: 'PUSH',
    /** 알림톡 우선, 미승인 템플릿은 SMS 폴백 (§8.2) */
    ALIMTALK: 'ALIMTALK',
    EMAIL: 'EMAIL',
};
export const CHANNEL_LABELS = {
    IN_APP: '인앱',
    PUSH: '푸시',
    ALIMTALK: '알림톡·문자',
    EMAIL: '이메일',
};
export const NOTIFICATION_EVENTS = {
    DAILY_NOTE_PUBLISHED: 'DAILY_NOTE_PUBLISHED',
    DAILY_NOTE_HOME_MESSAGE: 'DAILY_NOTE_HOME_MESSAGE',
    DAILY_NOTE_COMMENT: 'DAILY_NOTE_COMMENT',
    NOTICE_PUBLISHED: 'NOTICE_PUBLISHED',
    NOTICE_URGENT: 'NOTICE_URGENT',
    MEDICATION_REQUESTED: 'MEDICATION_REQUESTED',
    MEDICATION_ADMINISTERED: 'MEDICATION_ADMINISTERED',
    PICKUP_CHANGE_REQUESTED: 'PICKUP_CHANGE_REQUESTED',
    PICKUP_CHANGE_CONFIRMED: 'PICKUP_CHANGE_CONFIRMED',
    ATTENDANCE_CHECKED_IN: 'ATTENDANCE_CHECKED_IN',
    ATTENDANCE_CHECKED_OUT: 'ATTENDANCE_CHECKED_OUT',
    ATTENDANCE_MISSING: 'ATTENDANCE_MISSING',
    BUS_BOARDED: 'BUS_BOARDED',
    BUS_ALIGHTED: 'BUS_ALIGHTED',
    BUS_APPROACHING: 'BUS_APPROACHING',
    HEALTH_TEMPERATURE_ALERT: 'HEALTH_TEMPERATURE_ALERT',
    ALBUM_PUBLISHED: 'ALBUM_PUBLISHED',
    EVENT_CREATED: 'EVENT_CREATED',
    EVENT_REMINDER: 'EVENT_REMINDER',
    DEVELOPMENT_RECORD_PUBLISHED: 'DEVELOPMENT_RECORD_PUBLISHED',
    DEVELOPMENT_TASK_ASSIGNED: 'DEVELOPMENT_TASK_ASSIGNED',
    LIFE_RECORD_ISSUED: 'LIFE_RECORD_ISSUED',
    COUNSELING_REQUESTED: 'COUNSELING_REQUESTED',
    COUNSELING_CONFIRMED: 'COUNSELING_CONFIRMED',
    DOCUMENT_RENEWAL_REQUESTED: 'DOCUMENT_RENEWAL_REQUESTED',
    DOCUMENT_COLLECTION_DUE: 'DOCUMENT_COLLECTION_DUE',
    CONSENT_REQUESTED: 'CONSENT_REQUESTED',
    SUBSCRIPTION_EXPIRING: 'SUBSCRIPTION_EXPIRING',
    MESSAGE_RECEIVED: 'MESSAGE_RECEIVED',
    WEEKLY_DIGEST: 'WEEKLY_DIGEST',
    /** 신고 접수 — 원장에게 (§5.30) */
    CONTENT_REPORTED: 'CONTENT_REPORTED',
    /** 등하원 위치 공유 시작 — 보호자가 출발하면 담임에게 (§5.10 v1.22) */
    GUARDIAN_DEPARTED: 'GUARDIAN_DEPARTED',
};
const { IN_APP, PUSH, ALIMTALK, EMAIL } = NOTIFICATION_CHANNELS;
export const EVENT_CHANNEL_POLICY = {
    DAILY_NOTE_PUBLISHED: {
        event: 'DAILY_NOTE_PUBLISHED',
        label: '알림장 발행',
        defaultChannels: [IN_APP, PUSH],
        optionalChannels: [],
        forcePush: false,
        bypassQuietHours: false,
    },
    DAILY_NOTE_HOME_MESSAGE: {
        event: 'DAILY_NOTE_HOME_MESSAGE',
        // "가정통신"은 원→가정 발송(가정통신문)으로 오해되어 개명 (v1.10)
        label: '가정 소식 등록',
        defaultChannels: [IN_APP, PUSH],
        optionalChannels: [],
        forcePush: false,
        bypassQuietHours: false,
    },
    DAILY_NOTE_COMMENT: {
        event: 'DAILY_NOTE_COMMENT',
        label: '알림장 댓글',
        defaultChannels: [IN_APP],
        optionalChannels: [PUSH],
        forcePush: false,
        bypassQuietHours: false,
    },
    NOTICE_PUBLISHED: {
        event: 'NOTICE_PUBLISHED',
        label: '일반 공지',
        defaultChannels: [IN_APP, PUSH],
        optionalChannels: [EMAIL],
        forcePush: false,
        bypassQuietHours: false,
    },
    NOTICE_URGENT: {
        event: 'NOTICE_URGENT',
        label: '긴급 공지',
        defaultChannels: [IN_APP, PUSH, ALIMTALK, EMAIL],
        optionalChannels: [],
        forcePush: true,
        bypassQuietHours: true,
    },
    MEDICATION_REQUESTED: {
        event: 'MEDICATION_REQUESTED',
        label: '투약의뢰서 제출',
        defaultChannels: [IN_APP, PUSH],
        optionalChannels: [],
        forcePush: false,
        bypassQuietHours: false,
    },
    MEDICATION_ADMINISTERED: {
        event: 'MEDICATION_ADMINISTERED',
        label: '투약 완료',
        defaultChannels: [IN_APP, PUSH],
        optionalChannels: [ALIMTALK],
        forcePush: false,
        bypassQuietHours: false,
    },
    PICKUP_CHANGE_REQUESTED: {
        event: 'PICKUP_CHANGE_REQUESTED',
        label: '귀가 변경 요청',
        defaultChannels: [IN_APP, PUSH, ALIMTALK],
        optionalChannels: [],
        forcePush: true,
        bypassQuietHours: true,
    },
    PICKUP_CHANGE_CONFIRMED: {
        event: 'PICKUP_CHANGE_CONFIRMED',
        label: '귀가 변경 확정',
        defaultChannels: [IN_APP, PUSH, ALIMTALK],
        optionalChannels: [],
        forcePush: true,
        bypassQuietHours: true,
    },
    ATTENDANCE_CHECKED_IN: {
        event: 'ATTENDANCE_CHECKED_IN',
        label: '등원 완료',
        defaultChannels: [IN_APP],
        optionalChannels: [PUSH],
        forcePush: false,
        bypassQuietHours: false,
    },
    ATTENDANCE_CHECKED_OUT: {
        event: 'ATTENDANCE_CHECKED_OUT',
        label: '하원 완료',
        defaultChannels: [IN_APP],
        optionalChannels: [PUSH],
        forcePush: false,
        bypassQuietHours: false,
    },
    ATTENDANCE_MISSING: {
        event: 'ATTENDANCE_MISSING',
        label: '미등원·미신고 (교사)',
        defaultChannels: [IN_APP, PUSH],
        optionalChannels: [],
        forcePush: false,
        bypassQuietHours: false,
    },
    BUS_BOARDED: {
        event: 'BUS_BOARDED',
        label: '버스 승차 완료',
        defaultChannels: [IN_APP, PUSH],
        optionalChannels: [],
        forcePush: false,
        bypassQuietHours: false,
    },
    BUS_ALIGHTED: {
        event: 'BUS_ALIGHTED',
        label: '버스 하차 완료',
        defaultChannels: [IN_APP, PUSH],
        optionalChannels: [],
        forcePush: false,
        bypassQuietHours: false,
    },
    BUS_APPROACHING: {
        event: 'BUS_APPROACHING',
        label: '버스 도착 임박 (§5.20 P6)',
        defaultChannels: [IN_APP, PUSH],
        optionalChannels: [],
        forcePush: false,
        bypassQuietHours: false,
    },
    HEALTH_TEMPERATURE_ALERT: {
        event: 'HEALTH_TEMPERATURE_ALERT',
        label: '체온 임계값 초과',
        defaultChannels: [IN_APP, PUSH],
        optionalChannels: [ALIMTALK],
        forcePush: true,
        bypassQuietHours: true,
    },
    ALBUM_PUBLISHED: {
        event: 'ALBUM_PUBLISHED',
        label: '새 앨범',
        defaultChannels: [IN_APP, PUSH],
        optionalChannels: [],
        forcePush: false,
        bypassQuietHours: false,
    },
    EVENT_CREATED: {
        event: 'EVENT_CREATED',
        label: '일정 등록·변경',
        defaultChannels: [IN_APP, PUSH],
        optionalChannels: [],
        forcePush: false,
        bypassQuietHours: false,
    },
    EVENT_REMINDER: {
        event: 'EVENT_REMINDER',
        label: '준비물 D-1 리마인드',
        defaultChannels: [IN_APP, PUSH],
        optionalChannels: [],
        forcePush: false,
        bypassQuietHours: false,
    },
    DEVELOPMENT_RECORD_PUBLISHED: {
        event: 'DEVELOPMENT_RECORD_PUBLISHED',
        label: '발달기록 공개',
        defaultChannels: [IN_APP, PUSH],
        optionalChannels: [],
        forcePush: false,
        bypassQuietHours: false,
    },
    DEVELOPMENT_TASK_ASSIGNED: {
        event: 'DEVELOPMENT_TASK_ASSIGNED',
        label: '발달기록 태스크 (교사)',
        defaultChannels: [IN_APP, PUSH],
        optionalChannels: [],
        forcePush: false,
        bypassQuietHours: false,
    },
    LIFE_RECORD_ISSUED: {
        event: 'LIFE_RECORD_ISSUED',
        label: '생활기록부 발급',
        defaultChannels: [IN_APP, PUSH, EMAIL],
        optionalChannels: [],
        forcePush: false,
        bypassQuietHours: false,
    },
    COUNSELING_REQUESTED: {
        event: 'COUNSELING_REQUESTED',
        label: '면담 신청 (교직원)',
        defaultChannels: [IN_APP, PUSH],
        optionalChannels: [],
        forcePush: false,
        bypassQuietHours: false,
    },
    COUNSELING_CONFIRMED: {
        event: 'COUNSELING_CONFIRMED',
        label: '면담 확정·변경',
        defaultChannels: [IN_APP, PUSH],
        optionalChannels: [ALIMTALK],
        forcePush: false,
        bypassQuietHours: false,
    },
    DOCUMENT_RENEWAL_REQUESTED: {
        event: 'DOCUMENT_RENEWAL_REQUESTED',
        label: '갱신 서류 제출 요청',
        defaultChannels: [IN_APP, PUSH],
        optionalChannels: [EMAIL],
        forcePush: false,
        bypassQuietHours: false,
    },
    DOCUMENT_COLLECTION_DUE: {
        event: 'DOCUMENT_COLLECTION_DUE',
        label: '서류 수합 기한 임박 (교직원)',
        defaultChannels: [IN_APP, PUSH],
        optionalChannels: [],
        forcePush: false,
        bypassQuietHours: false,
    },
    CONSENT_REQUESTED: {
        event: 'CONSENT_REQUESTED',
        label: '전자동의서 요청',
        defaultChannels: [IN_APP, PUSH],
        optionalChannels: [EMAIL],
        forcePush: false,
        bypassQuietHours: false,
    },
    SUBSCRIPTION_EXPIRING: {
        event: 'SUBSCRIPTION_EXPIRING',
        label: '구독 만료 임박 (원장)',
        defaultChannels: [IN_APP, PUSH, EMAIL],
        optionalChannels: [],
        forcePush: false,
        bypassQuietHours: false,
    },
    MESSAGE_RECEIVED: {
        event: 'MESSAGE_RECEIVED',
        label: '쪽지 수신',
        defaultChannels: [IN_APP, PUSH],
        optionalChannels: [],
        forcePush: false,
        bypassQuietHours: false,
    },
    CONTENT_REPORTED: {
        event: 'CONTENT_REPORTED',
        label: '신고 접수',
        defaultChannels: [IN_APP, PUSH],
        optionalChannels: [],
        // 안전 관련 — 사용자 설정과 무관하게 푸시 (§8.2)
        forcePush: true,
        bypassQuietHours: false,
    },
    WEEKLY_DIGEST: {
        event: 'WEEKLY_DIGEST',
        label: '주간 요약 (학부모, 옵션)',
        defaultChannels: [],
        optionalChannels: [EMAIL],
        forcePush: false,
        bypassQuietHours: false,
    },
    GUARDIAN_DEPARTED: {
        event: 'GUARDIAN_DEPARTED',
        label: '보호자 출발 (등하원 위치 공유)',
        defaultChannels: [IN_APP, PUSH],
        optionalChannels: [],
        forcePush: false,
        bypassQuietHours: false,
    },
};
/**
 * 공지 추가 안내 채널 (§5.4 v1.22) — 일반 공지도 작성자가 고르면 알림톡(문자 폴백)·이메일로 함께 보낸다.
 * 긴급 공지는 정책상 이미 둘 다 나가므로 고를 필요가 없다. 각 가정의 채널 설정(끔)은 그대로 존중한다.
 */
export const NOTICE_EXTRA_CHANNELS = [ALIMTALK, EMAIL];
export const NOTICE_CHANNEL_COPY = {
    sectionTitle: '문자·이메일로도 안내',
    hint: '앱 알림에 더해 보내요. 앱을 자주 열지 않는 가정에도 닿게 하고 싶을 때 켜 주세요. 각 가정의 알림 설정(끔)은 그대로 지켜요.',
    urgentNote: '긴급 공지는 문자와 이메일로 함께 나가요.',
    sentBadge: { ALIMTALK: '문자 안내', EMAIL: '이메일 안내' },
};
/** 야간 보류 기본 구간 (§8.2) — 시설 설정으로 조정 가능 */
export const QUIET_HOURS_DEFAULT = { startHour: 21, endHour: 8 };
/**
 * 야간 보류 판단. Asia/Seoul 기준 시(hour)를 받아 판단한다.
 * 21:00–08:00 사이 수신분은 긴급 이벤트를 제외하고 아침에 일괄 발송한다.
 */
export function isWithinQuietHours(hour, quietHours = QUIET_HOURS_DEFAULT) {
    const { startHour, endHour } = quietHours;
    // 자정을 넘는 구간
    return startHour > endHour ? hour >= startHour || hour < endHour : hour >= startHour && hour < endHour;
}
/** 발송 실패 재시도 백오프 — 지수 백오프 3회 (§8.2) */
export const DELIVERY_RETRY_DELAYS_MS = [30_000, 120_000, 600_000];
export const DELIVERY_MAX_ATTEMPTS = DELIVERY_RETRY_DELAYS_MS.length + 1;
export const DELIVERY_STATUS = {
    PENDING: 'PENDING',
    HELD: 'HELD',
    SENT: 'SENT',
    FAILED: 'FAILED',
    SKIPPED: 'SKIPPED',
};
/**
 * 사용자 수신 설정을 반영한 최종 발송 채널 계산.
 * 안전 관련 이벤트(forcePush)는 사용자가 꺼도 푸시가 유지된다(§8.2).
 */
export function resolveChannels(event, preference) {
    const policy = EVENT_CHANNEL_POLICY[event];
    if (!policy)
        return [IN_APP];
    const candidates = new Set(policy.defaultChannels);
    for (const channel of policy.optionalChannels) {
        if (preference?.[channel])
            candidates.add(channel);
    }
    const resolved = [...candidates].filter((channel) => {
        // 인앱 알림센터는 항상 남긴다(기록 성격).
        if (channel === IN_APP)
            return true;
        if (channel === PUSH && policy.forcePush)
            return true;
        return preference?.[channel] !== false;
    });
    return resolved;
}
/**
 * 알림 템플릿 변수 치환 — `{원아명} {반} {일시}` 형태 (§8.2).
 * 변수명이 한글이므로 `\w`가 아닌 "중괄호·공백이 아닌 문자"로 매칭한다.
 */
export function renderTemplate(template, vars) {
    return template.replace(/\{([^{}\s]+)\}/g, (match, key) => key in vars ? String(vars[key]) : match);
}
//# sourceMappingURL=notifications.js.map